// ════════════════════════════════════════════════════════════════════════
//  Assistent „Neue Lektion“
//
//  Vorher hieß der Weg „Lektion anhängen“: ein Klick, danach eine Lektion
//  namens `lektion-11` mit Platzhaltern in jedem Feld – gültig, veröffentlichbar
//  und in nichts wie der Bestand, der `grundreihe-4a` heißt. Was zu tun blieb,
//  stand in der Anleitung, nicht auf dem Bildschirm.
//
//  Der Assistent kehrt das um: er fragt in der Reihenfolge, in der die Dinge
//  voneinander abhängen (erst die Tasten, dann die Texte, die sie benutzen), und
//  schreibt erst im letzten Schritt in den Entwurf. Abbrechen hinterlässt nichts.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fingerIndex } from "@otto/lehrgang";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { coursePath, useCourseSlug } from "../../../shared/lib/coursePath.js";
import { NoDraft } from "../../draft/components/NoDraft.js";
import { StagePreview } from "../../preview/components/StagePreview.js";
import {
  STEPS,
  effectiveSubtitle,
  emptyLesson,
  label,
  stepProblems,
  toLesson,
  type NewLesson,
  type Step,
} from "../lib/newLesson.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./wizard.module.css";

export function NewLessonWizard() {
  const slug = useCourseSlug();
  const { bundle, setBundle, publishedStageKeys } = useDraft();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("Herkunft");
  const [state, setState] = useState<NewLesson>(emptyLesson);

  if (!bundle) return <NoDraft />;

  const set = (part: Partial<NewLesson>) => setState((s) => ({ ...s, ...part }));
  const problems = stepProblems(step, state, bundle);
  const at = STEPS.indexOf(step);

  const apply = () => {
    const lesson = toLesson(state, bundle, publishedStageKeys);
    setBundle({ ...bundle, lessons: [...bundle.lessons, lesson] });
    navigate(coursePath(slug, `lektionen/${lesson.key}`));
  };

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>Neue Lektion</h1>

      <ol className={own.steps}>
        {STEPS.map((name, i) => (
          <li
            key={name}
            className={`${own.step} ${i === at ? own.stepNow : ""} ${i < at ? own.stepDone : ""}`}
          >
            <span className={own.stepNumber}>{i + 1}</span>
            {name}
          </li>
        ))}
      </ol>

      {step === "Herkunft" && <Origin state={state} set={set} />}
      {step === "Tasten" && <Keys state={state} set={set} />}
      {step === "Einführung" && <Intro state={state} set={set} />}
      {step === "Stufen" && <Stages state={state} set={set} />}
      {step === "Prüfen" && <Review state={state} />}

      {problems.length > 0 && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          <ul className={own.problems}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`${styles.row} ${styles.spaced}`}>
        <button
          type="button"
          className={styles.buttonQuiet}
          disabled={at === 0}
          onClick={() => setStep(STEPS[at - 1]!)}
        >
          Zurück
        </button>

        {step === "Prüfen" ? (
          <button type="button" className={styles.button} onClick={apply}>
            Lektion übernehmen
          </button>
        ) : (
          <button
            type="button"
            className={styles.button}
            disabled={problems.length > 0}
            title={problems.length > 0 ? "Erst die offenen Punkte" : "Weiter"}
            onClick={() => setStep(STEPS[at + 1]!)}
          >
            Weiter
          </button>
        )}

        <button
          type="button"
          className={styles.buttonQuiet}
          style={{ marginLeft: "auto" }}
          onClick={() => navigate(coursePath(slug, "lektionen"))}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}

type StepProps = {
  state: NewLesson;
  set: (part: Partial<NewLesson>) => void;
};

/**
 * Schritt 1: woher die Lektion kommt.
 *
 * Der Buchabschnitt ist FREIER TEXT. Eine hinterlegte Liste wäre heute
 * vollständig und mit Kapitel 6 falsch – und Inhalt gehört nicht in den Code.
 */
function Origin({ state, set }: StepProps) {
  return (
    <>
      <p className={styles.subtitle}>
        Der Buchabschnitt macht die Lektion auffindbar: „macht 4c“ muss im Heft und in der
        App dasselbe sein.
      </p>

      <div className={own.pair}>
        <label>
          Buchabschnitt (frei, z. B. „4c“ – oder leer)
          <input
            className={styles.field}
            value={state.section}
            onChange={(e) => set({ section: e.target.value })}
          />
        </label>
        <label>
          Titel der Lektion
          <input
            className={styles.field}
            value={state.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </label>
      </div>

      <p className={own.note}>
        Einen <strong>Schlüssel</strong> vergibt die App selbst: einen Zufallswert, der
        mit der Lektion mitwandert – durch jedes Umsortieren, jede Umbenennung und jede
        neue Fassung. An ihm hängen später die Fortschrittszeilen der Kinder, und deshalb
        steht in ihm nichts, was sich ändern kann.
      </p>
    </>
  );
}

/**
 * Schritt 2: die Tasten – und die Finger, die ihnen fehlen.
 *
 * Der häufigste Redaktionsfehler ist ein Zeichen ohne Finger-Zuordnung. Er fällt
 * sonst erst an einem Übungstext auf, also drei Schritte später und an einer
 * anderen Stelle. Hier fällt er auf, bevor es einen Text gibt – und wird an
 * derselben Stelle behoben.
 */
function Keys({ state, set }: StepProps) {
  const { bundle, setBundle } = useDraft();
  // Welche Taste gerade zugeordnet wird und welchem Finger.
  const [assigning, setAssigning] = useState<string | null>(null);
  const [fingerId, setFingerId] = useState("");
  // Groß- und Kleinbuchstabe sind zwei Tasten – das stand als Satz da, ohne
  // einen Weg, den großen anzuklicken. Die Umschalttaste ist dieser Weg und
  // funktioniert wie die echte: gedrückt zeigt das Bild die Großbuchstaben.
  const [shift, setShift] = useState(false);
  if (!bundle) return null;

  const fingers = fingerIndex(bundle);
  const chosen = new Set(state.keys);

  const toggle = (key: string) =>
    set({
      keys: chosen.has(key) ? state.keys.filter((k) => k !== key) : [...state.keys, key],
    });

  // Zuordnen, nicht anlegen: die Finger stehen fest, die Taste wandert in die
  // Liste eines von ihnen. Vorher entstand hier ein neuer Finger mit eigener
  // Farbe – und damit eine zehnte Farbe für dieselbe Hand.
  const assign = (key: string) => {
    const target = bundle.fingers.find((f) => f.id === fingerId);
    if (!target) return;
    setBundle({
      ...bundle,
      fingers: bundle.fingers.map((f) =>
        f.id === target.id ? { ...f, keys: [...f.keys, key] } : f,
      ),
    });
    setAssigning(null);
    setFingerId("");
  };

  return (
    <>
      <p className={styles.subtitle}>
        Anklicken, was diese Lektion übt. Groß- und Kleinbuchstaben sind zwei Tasten: für
        den großen erst die Umschalttaste drücken.
      </p>

      <div className={own.keyboard}>
        {/* Die Leertastenreihe wird übersprungen: sie enthält nur die Leertaste,
            und die steht unten neben der Umschalttaste – dort, wo sie hingehört
            und wo man sie trifft. Als schmale Taste in einer eigenen Reihe stand
            sie zweimal da. */}
        {bundle.keyboard.rows
          .filter((row) => row.id !== "leer")
          .map((row) => (
            <div key={row.id} className={own.keyRow}>
              {row.keys.map((key, j) => {
                // Was die Taste im aktuellen Zustand liefert. Bei Zeichen ohne
                // Großform (Komma, Punkt) ist das dasselbe – wie auf der echten
                // Tastatur, nur dass dort noch andere Zeichen liegen.
                const shifted = shift ? key.toUpperCase() : key;
                return (
                  <button
                    type="button"
                    key={`${key}-${j}`}
                    className={`${own.key} ${chosen.has(shifted) ? own.keyOn : ""}`}
                    aria-pressed={chosen.has(shifted)}
                    onClick={() => toggle(shifted)}
                  >
                    {label(shifted)}
                  </button>
                );
              })}
            </div>
          ))}
        <div className={own.keyRow}>
          <button
            type="button"
            className={`${own.key} ${own.wide} ${shift ? own.keyShift : ""}`}
            aria-pressed={shift}
            onClick={() => setShift(!shift)}
          >
            ⇧ Umschalt
          </button>
          <button
            type="button"
            className={`${own.key} ${own.space} ${chosen.has(" ") ? own.keyOn : ""}`}
            aria-pressed={chosen.has(" ")}
            onClick={() => toggle(" ")}
          >
            Leertaste ␣
          </button>
        </div>
      </div>

      <p className={own.note}>
        Gewählt: <span className="mono">{state.keys.map(label).join(", ") || "–"}</span>
      </p>

      {state.keys
        .filter((key) => !fingers.has(key))
        .map((key) => (
          <div key={key} className={`${styles.notice} ${styles.noticeInfo} ${own.fix}`}>
            Die Taste <strong>{label(key)}</strong> gehört noch zu keinem Finger.
            {assigning === key ? (
              <span className={own.fixRow}>
                <select
                  className={styles.field}
                  aria-label={`Finger für ${label(key)}`}
                  value={fingerId}
                  onChange={(e) => setFingerId(e.target.value)}
                >
                  <option value="">Finger wählen …</option>
                  {bundle.fingers.map((finger) => (
                    <option key={finger.id} value={finger.id}>
                      {finger.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.button}
                  disabled={!fingerId}
                  onClick={() => assign(key)}
                >
                  Übernehmen
                </button>
              </span>
            ) : (
              <button
                type="button"
                className={styles.buttonQuiet}
                onClick={() => setAssigning(key)}
              >
                Finger zuordnen
              </button>
            )}
          </div>
        ))}
    </>
  );
}

/** Schritt 3: was das Kind vor dem Tippen liest. */
function Intro({ state, set }: StepProps) {
  return (
    <>
      <label className={own.block}>
        Untertitel
        <input
          className={styles.field}
          aria-label="Untertitel"
          value={effectiveSubtitle(state)}
          onChange={(e) => set({ subtitle: e.target.value, subtitleEdited: true })}
        />
        <span className={own.note}>
          Vorgeschlagen als „Heft 4c · Titel“ – so findet die Lehrkraft die Seite im Heft
          wieder.
        </span>
      </label>

      <label className={own.block}>
        Einleitung
        <textarea
          className={`${styles.field} ${styles.multiline}`}
          value={state.intro}
          onChange={(e) => set({ intro: e.target.value })}
        />
      </label>

      <div className={own.pair}>
        <label>
          Farbe der Lektion
          <input
            type="color"
            className={own.color}
            aria-label="Farbe der Lektion"
            value={state.color}
            onChange={(e) => set({ color: e.target.value.toUpperCase() })}
          />
        </label>
        <label>
          Großbuchstaben entstehen mit
          <select
            className={styles.field}
            value={state.uppercaseWith ?? ""}
            onChange={(e) =>
              set(
                e.target.value === ""
                  ? { uppercaseWith: undefined }
                  : { uppercaseWith: e.target.value as "shift" | "capsLock" },
              )
            }
          >
            <option value="">egal – die Lektion prüft es nicht</option>
            <option value="shift">Umschalttaste</option>
            <option value="capsLock">Feststelltaste</option>
          </select>
          <span className={own.note}>
            Nur setzen, wenn genau dieser Weg der Inhalt der Lektion ist.
          </span>
        </label>
      </div>
    </>
  );
}

/** Schritt 4: die Übungszeilen, geprüft beim Tippen. */
function Stages({ state, set }: StepProps) {
  const { bundle } = useDraft();
  if (!bundle) return null;

  const chosen = new Set(state.keys);
  const change = (i: number, part: Partial<NewLesson["stages"][number]>) =>
    set({ stages: state.stages.map((s, j) => (j === i ? { ...s, ...part } : s)) });

  return (
    <>
      <div className={`${styles.notice} ${styles.noticeInfo}`}>
        Übungstexte nicht ohne Rücksprache mit der Lehrkraft ändern – das Wortmaterial ist
        auf das Lehrbuch und auf die Lesekompetenz Klasse 3 abgestimmt. Das gilt auch für
        die Aufteilung auf Stufen.
      </div>

      {state.stages.map((stage, i) => {
        const missing = [...new Set(stage.text)].filter((c) => !chosen.has(c));
        return (
          <div key={i} className={own.stage}>
            <div className={own.stageHead}>
              <strong>Stufe {i + 1}</strong>
              {state.stages.length > 1 && (
                <button
                  type="button"
                  className={styles.buttonQuiet}
                  onClick={() => set({ stages: state.stages.filter((_, j) => j !== i) })}
                >
                  Entfernen
                </button>
              )}
            </div>
            <input
              className={styles.field}
              aria-label={`Titel Stufe ${i + 1}`}
              placeholder="Worum es in dieser Zeile geht"
              value={stage.title}
              onChange={(e) => change(i, { title: e.target.value })}
            />
            <input
              className={styles.field}
              aria-label={`Hinweis Stufe ${i + 1}`}
              placeholder="Ein Satz an das Kind, steht über der Übung"
              value={stage.hint}
              onChange={(e) => change(i, { hint: e.target.value })}
            />
            <textarea
              className={`${styles.field} ${styles.multiline}`}
              aria-label={`Übungstext Stufe ${i + 1}`}
              value={stage.text}
              onChange={(e) => change(i, { text: e.target.value })}
            />

            {missing.length > 0 && (
              <div className={`${styles.notice} ${styles.noticeError} ${own.fix}`}>
                Nicht im Tastenvorrat dieser Lektion:{" "}
                <strong>{missing.map(label).join(" ")}</strong>
                <button
                  type="button"
                  className={styles.buttonQuiet}
                  onClick={() => set({ keys: [...state.keys, ...missing] })}
                >
                  Tasten aufnehmen
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className={`${styles.buttonQuiet} ${styles.spaced}`}
        onClick={() =>
          set({ stages: [...state.stages, { title: "", hint: "", text: "" }] })
        }
      >
        Stufe anhängen
      </button>
    </>
  );
}

/** Schritt 5: alles noch einmal, so wie es das Kind sieht. */
function Review({ state }: { state: NewLesson }) {
  const { bundle } = useDraft();
  if (!bundle) return null;
  const lesson = toLesson(state, bundle);

  return (
    <>
      <p className={styles.subtitle}>
        Beim Übernehmen wandert die Lektion ans Ende des Entwurfs. Gespeichert ist damit
        noch nichts – das tut die Fassungsleiste.
      </p>

      <dl className={own.facts}>
        <div>
          <dt>Untertitel</dt>
          <dd>{lesson.subtitle}</dd>
        </div>
        <div>
          <dt>Tasten</dt>
          <dd className="mono">{lesson.keys.map(label).join(", ")}</dd>
        </div>
        <div>
          <dt>Stufen</dt>
          <dd>{lesson.stages.length}</dd>
        </div>
      </dl>

      {lesson.stages.map((stage) => (
        <div key={stage.key} className={own.stage}>
          <strong>{stage.title}</strong>
          <StagePreview bundle={bundle} lesson={lesson} stage={stage} />
        </div>
      ))}
    </>
  );
}
